/**
 * Captures a SID tune as the stream of register writes its player makes, frame by frame
 * (spec 07 §3). The player runs on `Cpu6502` over flat RAM: the Bubble Bobble player needs no
 * ROM, CIA, VIC or interrupts, and never reads the SID back.
 *
 * Writes are kept in order within a frame (a "hard restart" turns a gate off and on again in
 * one frame, and only the order says so); a write that does not change the register is
 * dropped, because it changes nothing the chip does. The loop is found by hashing the
 * player's whole state after every frame: the first repeat is where the tune loops.
 */

import { Cpu6502 } from './cpu6502';
import type { PsidTune } from './psid';

/** SID registers 0x00..0x18; the rest of $D400-$D7FF mirrors them or is read-only. */
export const SID_REGISTERS = 25;

export interface CapturedTune {
  subtune: number;
  /** Writes made by the init routine, flat [register, value, ...]. */
  initWrites: number[];
  /** Per 50 Hz frame, the writes made by one call of the play routine, flat [register, value, ...]. */
  frames: number[][];
  /** Index of the frame playback returns to after the last one, or -1 if the tune ends. */
  loopFrame: number;
}

function fnv(bytes: Uint8Array, from: number, to: number, seed: number): number {
  let h = seed;
  for (let i = from; i < to; i++) {
    h ^= bytes[i] ?? 0;
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h;
}

/** Run subtune `subtune` (1-based) for at most `maxFrames` frames, stopping at its loop or end. */
export function captureSubtune(tune: PsidTune, subtune: number, maxFrames: number): CapturedTune {
  const ram = new Uint8Array(0x10000);
  ram.set(tune.data, tune.loadAddress);
  const shadow = new Int16Array(SID_REGISTERS).fill(-1);
  let pending: number[] = [];
  const cpu = new Cpu6502({
    read: (a) => ram[a] ?? 0,
    write: (a, v) => {
      if (a >= 0xd400 && a <= 0xd7ff) {
        const reg = a & 0x1f;
        if (reg < SID_REGISTERS && shadow[reg] !== v) {
          shadow[reg] = v;
          pending.push(reg, v);
        }
        return;
      }
      ram[a] = v;
    },
  });
  cpu.sp = 0xff;
  cpu.call(tune.initAddress, subtune - 1);
  const initWrites = pending;
  const dataEnd = Math.min(0x10000, tune.loadAddress + tune.data.length);
  const stateHash = (): number => {
    let h = fnv(ram, 0, 0x200, 0x811c9dc5);
    h = fnv(ram, tune.loadAddress, dataEnd, h);
    for (let r = 0; r < SID_REGISTERS; r++) h = Math.imul(h ^ ((shadow[r] ?? 0) & 0xffff), 0x01000193) >>> 0;
    return h;
  };
  const seen = new Map<number, number>();
  seen.set(stateHash(), -1);
  const frames: number[][] = [];
  for (let f = 0; f < maxFrames; f++) {
    pending = [];
    cpu.sp = 0xff;
    cpu.call(tune.playAddress);
    frames.push(pending);
    const h = stateHash();
    const before = seen.get(h);
    if (before !== undefined) {
      // State after frame f equals state after frame `before`: frames before+1 .. f repeat.
      const loop = frames.slice(before + 1);
      const silent = loop.every((w) => w.length === 0) && gatesOff(shadow);
      if (silent) return { subtune, initWrites, frames: frames.slice(0, before + 1), loopFrame: -1 };
      return { subtune, initWrites, frames, loopFrame: before + 1 };
    }
    seen.set(h, f);
  }
  throw new Error(`subtune ${subtune}: no loop or end within ${maxFrames} frames`);
}

function gatesOff(shadow: Int16Array): boolean {
  return [4, 11, 18].every((r) => ((shadow[r] ?? 0) & 1) === 0);
}

/** Pack frames as bytes: per frame a write count, then that many (register, value) pairs. */
export function packFrames(frames: readonly number[][]): Uint8Array {
  const size = frames.reduce((n, w) => n + 1 + w.length, 0);
  const out = new Uint8Array(size);
  let i = 0;
  for (const w of frames) {
    if (w.length / 2 > 255) throw new Error('more than 255 writes in one frame');
    out[i++] = w.length / 2;
    for (const b of w) out[i++] = b;
  }
  return out;
}

/** Inverse of `packFrames`. */
export function unpackFrames(bytes: Uint8Array): number[][] {
  const frames: number[][] = [];
  let i = 0;
  while (i < bytes.length) {
    const n = bytes[i++] ?? 0;
    frames.push(Array.from(bytes.subarray(i, i + n * 2)));
    i += n * 2;
  }
  return frames;
}
